import { Plus, Loader2 } from 'lucide-react';
import { useState, useRef } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface AddItemFormProps {
  onAdd: (input: { name: string; qty?: string }) => Promise<void>;
  disabled?: boolean;
}

/**
 * Compact two-field form: a name input and an inline qty input.
 * The qty input is invisible until the user types something into name —
 * keeps the empty state minimal but never blocks an experienced user.
 */
export function AddItemForm({ onAdd, disabled }: AddItemFormProps) {
  const [name, setName] = useState('');
  const [qty, setQty] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || submitting || disabled) return;

    setSubmitting(true);
    try {
      await onAdd({ name: trimmed, qty: qty.trim() || undefined });
      setName('');
      setQty('');
      // Refocus so the user can keep adding items in succession.
      nameRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'group flex items-center gap-2 rounded-2xl border border-border/60 bg-card/80',
        'p-1.5 pl-4 backdrop-blur-sm transition-all',
        'focus-within:border-primary/60 focus-within:reef-glow',
      )}
    >
      <Input
        ref={nameRef}
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Add an item…"
        disabled={disabled}
        className={cn(
          'h-10 flex-1 border-0 bg-transparent px-0 text-base',
          'focus-visible:ring-0 focus-visible:ring-offset-0',
          'placeholder:text-muted-foreground/70',
        )}
        aria-label="Item name"
      />

      <Input
        value={qty}
        onChange={(e) => setQty(e.target.value)}
        placeholder="qty"
        disabled={disabled}
        className={cn(
          'h-10 w-16 border-0 bg-transparent px-2 text-sm text-center',
          'focus-visible:ring-0 focus-visible:ring-offset-0',
          'placeholder:text-muted-foreground/50',
          'transition-all',
          name.length === 0 && 'opacity-0 pointer-events-none w-0',
        )}
        aria-label="Quantity"
        tabIndex={name.length === 0 ? -1 : 0}
      />

      <Button
        type="submit"
        size="icon"
        disabled={!name.trim() || submitting || disabled}
        className={cn(
          'size-10 rounded-xl shrink-0',
          'bg-primary text-primary-foreground',
          'hover:bg-primary/90 disabled:opacity-40',
          'transition-transform active:scale-95',
        )}
        aria-label="Add item"
      >
        {submitting ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Plus className="size-5" />
        )}
      </Button>
    </form>
  );
}
